/**
 * Profile configuration: the things the profile page renders that aren't yet
 * driven by data from the API.
 *
 * Two of these are deliberate switches rather than gaps:
 *
 *	FIRM_SIGN_IN_METHOD		Whether the firm signs in with a Lotus password or
 *							through single sign-on. SSO isn't built yet, but the
 *							whole profile page (and the Security card in
 *							particular) is written for both, so switching is a
 *							one-line change here once SSO lands.
 *
 *	notificationCatalogue	The notifications a person can turn on and off, and
 *							which capability each one depends on. A row a person
 *							can't act on is ABSENT, never disabled - a timekeeper
 *							who doesn't approve time simply has no approver row.
 */

/**
 * How this firm signs in. One of the keys of `signInMethods` in
 * /helpers/session-display-values.js: "LotusPassword" | "SingleSignOn".
 *
 * Change this one value to see the SSO profile. Everything downstream - the
 * page intro, the Security card, whether the change-password form renders -
 * follows from it.
 *
 * TODO: comes from the account's configuration once the API exposes it.
 */
export const FIRM_SIGN_IN_METHOD = "LotusPassword";


/**
 * Capability codes a notification row can depend on. These mirror what the firm
 * context reports about the person, so a row appears only when it can do
 * something for them.
 */
export const PROFILE_CAPABILITY = {
	// The person reviews time submitted by others.
	Approver: "Approver",

	// Lotus records arrivals and departures at client sites for this person.
	SiteVisitCapture: "SiteVisitCapture",
};


/**
 * Every notification the portal can send, in the order they're shown.
 *
 *	Code			Sent to the API as `NotificationCode`.
 *	Label / Desc	What the person reads.
 *	RequiresCapability	null for everyone; otherwise a PROFILE_CAPABILITY code.
 */
export const notificationCatalogue = [
	{
		Code: "TimesheetSubmissionReminders",
		Label: "Timesheet submission reminders",
		Description: "A nudge when a timesheet is due.",
		RequiresCapability: null,
	},
	{
		Code: "ApprovalOutcomes",
		Label: "Approval outcomes",
		Description: "When your time is approved or sent back.",
		RequiresCapability: null,
	},
	{
		Code: "SubmissionsAwaitingReview",
		Label: "New submissions awaiting review",
		Description: "When someone submits time for you to approve.",
		RequiresCapability: PROFILE_CAPABILITY.Approver,
	},
	{
		Code: "ArrivalAndDeparture",
		Label: "Arrival and departure notifications",
		Description: "When Lotus records you arriving at or leaving a site.",
		RequiresCapability: PROFILE_CAPABILITY.SiteVisitCapture,
	},
	{
		Code: "StaleVisitNudges",
		Label: "Stale-visit nudges",
		Description: "When a visit's stayed open longer than expected.",
		RequiresCapability: PROFILE_CAPABILITY.SiteVisitCapture,
	},
];




//-- Methods

/**
 * The notification rows this person should see, given what they can do.
 *
 * @param {string[]} capabilities PROFILE_CAPABILITY codes the person has.
 * @returns {object[]} The catalogue entries that apply to them.
 */
export function notificationsForCapabilities(capabilities)
{
	const held = capabilities ? capabilities : [];

	return notificationCatalogue.filter((n) => n.RequiresCapability == null || held.includes(n.RequiresCapability));
}
